import React, { useState, useEffect } from 'react';
import { useNavigate, Link, useSearchParams } from 'react-router-dom';
import { Shield, AlertCircle, Loader2, KeyRound, Clock } from 'lucide-react';
import { Button } from '@/components/ui/button';
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from '@/components/ui/card';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Alert, AlertDescription } from '@/components/ui/alert';
import { useAuth } from '@/contexts/AuthContext';

const LoginPage = () => {
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const { login, isAuthenticated, user } = useAuth();
  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [mfaCode, setMfaCode] = useState('');
  const [showMfa, setShowMfa] = useState(false);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');
  const [sessionExpired, setSessionExpired] = useState(false);
  const [lockedUntil, setLockedUntil] = useState(0); // epoch ms when unlock happens
  const [lockRemaining, setLockRemaining] = useState(0);

  // Check if redirected due to session expiry
  useEffect(() => {
    if (searchParams.get('expired') === 'true') {
      setSessionExpired(true);
    }
  }, [searchParams]);

  // Tick the lock countdown once per second
  useEffect(() => {
    if (!lockedUntil) return undefined;
    const tick = () => {
      const remaining = Math.max(0, Math.ceil((lockedUntil - Date.now()) / 1000));
      setLockRemaining(remaining);
      if (remaining <= 0) {
        setLockedUntil(0);
        setError('');
      }
    };
    tick();
    const id = setInterval(tick, 1000);
    return () => clearInterval(id);
  }, [lockedUntil]);

  // Redirect if already authenticated
  useEffect(() => {
    if (isAuthenticated) {
      if (user?.must_change_password) {
        navigate('/alterar-password');
      } else if (user?.is_cogniseg_global) {
        navigate('/cogniseg');
      } else {
        navigate(user?.role === 'super_admin' ? '/super-admin' : '/admin');
      }
    }
  }, [isAuthenticated, user, navigate]);

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (lockRemaining > 0) return;
    setError('');
    setLoading(true);

    const result = await login(email, password, null, showMfa ? mfaCode : null);
    
    if (result.success) {
      if (result.mustChangePassword) {
        navigate('/alterar-password');
      } else if (result.isCognisegGlobal) {
        navigate('/cogniseg');
      } else {
        navigate(result.role === 'super_admin' ? '/super-admin' : '/admin');
      }
    } else if (result.mfaRequired) {
      setShowMfa(true);
      setError('');
    } else if (result.rateLimited) {
      const seconds = result.retryAfter && result.retryAfter > 0 ? result.retryAfter : 15 * 60;
      setLockedUntil(Date.now() + seconds * 1000);
      setError(result.error);
    } else {
      setError(result.error);
    }
    
    setLoading(false);
  };

  const handleBackToLogin = () => {
    setShowMfa(false);
    setMfaCode('');
    setError('');
  };

  return (
    <div className="min-h-screen bg-slate-100 flex items-center justify-center p-4">
      <div className="w-full max-w-md">
        <div className="text-center mb-8">
          <Link to="/" className="inline-flex items-center gap-2">
            <Shield className="w-10 h-10 text-slate-900" />
            <span className="text-2xl font-bold text-slate-900" style={{ fontFamily: 'Plus Jakarta Sans' }}>
              COGNIEtica
            </span>
          </Link>
        </div>

        <Card>
          <CardHeader className="text-center">
            <CardTitle>{showMfa ? 'Verificação MFA' : 'Área Reservada'}</CardTitle>
            <CardDescription>
              {showMfa 
                ? 'Introduza o código da sua app de autenticação' 
                : 'Acesso para gestores de compliance'}
            </CardDescription>
          </CardHeader>
          <CardContent>
            {sessionExpired && (
              <Alert className="mb-6 bg-amber-50 border-amber-200">
                <Clock className="w-4 h-4 text-amber-600" />
                <AlertDescription className="text-amber-700">
                  A sua sessão expirou por motivos de segurança. Por favor, inicie sessão novamente.
                </AlertDescription>
              </Alert>
            )}

            {error && !lockRemaining && (
              <Alert className="mb-6 bg-red-50 border-red-200">
                <AlertCircle className="w-4 h-4 text-red-600" />
                <AlertDescription className="text-red-700">{error}</AlertDescription>
              </Alert>
            )}

            {lockRemaining > 0 && (
              <Alert className="mb-6 bg-red-50 border-red-200" data-testid="login-lockout">
                <AlertCircle className="w-4 h-4 text-red-600" />
                <AlertDescription className="text-red-700 space-y-1">
                  <div><strong>Demasiadas tentativas de login.</strong></div>
                  <div>Por segurança, o acesso a partir deste ponto está temporariamente bloqueado.</div>
                  <div className="pt-1">
                    Tenta novamente em{' '}
                    <span className="font-mono font-bold" data-testid="lockout-countdown">
                      {Math.floor(lockRemaining / 60)}:{String(lockRemaining % 60).padStart(2, '0')}
                    </span>
                    {' '}(min:seg).
                  </div>
                  <div className="w-full bg-red-200 rounded-full h-1.5 overflow-hidden mt-2">
                    <div
                      className="h-full bg-red-500 transition-all duration-1000 ease-linear"
                      style={{ width: `${Math.min(100, (lockRemaining / (15 * 60)) * 100)}%` }}
                    />
                  </div>
                </AlertDescription>
              </Alert>
            )}

            {!showMfa ? (
              // Login form
              <form onSubmit={handleSubmit} className="space-y-4">
                <div>
                  <Label htmlFor="email">Email</Label>
                  <Input
                    id="email"
                    type="email"
                    value={email}
                    onChange={(e) => setEmail(e.target.value)}
                    placeholder="email@empresa.com"
                    required
                    data-testid="login-email-input"
                  />
                </div>
                <div>
                  <Label htmlFor="password">Password</Label>
                  <Input
                    id="password"
                    type="password"
                    value={password}
                    onChange={(e) => setPassword(e.target.value)}
                    placeholder="••••••••"
                    required
                    data-testid="login-password-input"
                  />
                </div>
                <Button type="submit" className="w-full" disabled={loading || lockRemaining > 0} data-testid="login-submit-btn">
                  {loading ? (
                    <>
                      <Loader2 className="w-4 h-4 mr-2 animate-spin" />
                      A entrar...
                    </>
                  ) : lockRemaining > 0 ? (
                    `Bloqueado (${Math.floor(lockRemaining / 60)}:${String(lockRemaining % 60).padStart(2, '0')})`
                  ) : (
                    'Entrar'
                  )}
                </Button>
                <div className="text-center">
                  <Link
                    to="/recuperar-password"
                    className="text-sm text-slate-500 hover:text-slate-700"
                    data-testid="forgot-password-link"
                  >
                    Esqueceu-se da password?
                  </Link>
                </div>
              </form>
            ) : (
              // MFA form
              <form onSubmit={handleSubmit} className="space-y-4">
                <div className="text-center mb-4">
                  <div className="w-16 h-16 rounded-full bg-slate-100 flex items-center justify-center mx-auto mb-4">
                    <KeyRound className="w-8 h-8 text-slate-600" />
                  </div>
                </div>
                <div>
                  <Label htmlFor="mfaCode">Código de 6 dígitos</Label>
                  <Input
                    id="mfaCode"
                    type="text"
                    value={mfaCode}
                    onChange={(e) => setMfaCode(e.target.value.replace(/\D/g, '').slice(0, 6))}
                    placeholder="000000"
                    required
                    className="text-center text-2xl tracking-widest font-mono"
                    autoComplete="one-time-code"
                    data-testid="login-mfa-input"
                  />
                  <p className="text-xs text-slate-500 mt-2 text-center">
                    Ou introduza um código de backup
                  </p>
                </div>
                <Button type="submit" className="w-full" disabled={loading || mfaCode.length < 6} data-testid="login-mfa-submit-btn">
                  {loading ? (
                    <>
                      <Loader2 className="w-4 h-4 mr-2 animate-spin" />
                      A verificar...
                    </>
                  ) : (
                    'Verificar'
                  )}
                </Button>
                <Button 
                  type="button" 
                  variant="outline" 
                  className="w-full" 
                  onClick={handleBackToLogin}
                >
                  Voltar ao login
                </Button>
              </form>
            )}
          </CardContent>
        </Card>

        <div className="mt-6 text-center">
          <Link to="/" className="text-sm text-slate-500 hover:text-slate-700">
            ← Voltar ao início
          </Link>
        </div>

        <footer className="mt-8 text-center" data-testid="login-footer">
          <p className="text-xs text-slate-400">
            © {new Date().getFullYear()} CogniEtica - Canal de denúncias by{' '}
            <a
              href="https://cogniseg.com"
              target="_blank"
              rel="noopener noreferrer"
              className="font-medium text-slate-500 hover:text-slate-700 underline"
              data-testid="footer-cogniseg-link"
            >
              Cogniseg
            </a>
            <br />
            Todos os direitos reservados.
          </p>
        </footer>
      </div>
    </div>
  );
};

export default LoginPage;
